import { Franchise } from '@/types';
import { Input, Modal, Typography } from 'antd';
import { FC, useEffect, useState } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import './index.scss';
import searchImg from '@/assets/images/search-lg.png';
import { getFranchisesWithoutFranchableName } from '@/api/consultants';
import { useDebounce } from '@/hooks/useDebounce';
import { useQuery } from '@tanstack/react-query';

interface Props {
  open: boolean;
  onClose: () => void;
  onSave: (franchiseId: string, franchableName: string) => void;
  saving?: boolean;
  franchise?: Franchise | null;
  type?: string;
}

const AddBrandMappingModal: FC<Props> = ({
  open,
  onClose,
  onSave,
  saving = false,
  franchise = null,
  type = '',
}) => {
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const { data } = useQuery<
    {
      franchises: Franchise[];
    }
  >({
    queryKey: ['franchisors-unmapped', debouncedSearch],
    queryFn: () =>
      getFranchisesWithoutFranchableName({
        search: debouncedSearch,
        limit: 500,
      }),
    enabled: open,
  });
  const [selectedFranchise, setSelectedFranchise] = useState<Franchise | null>(
    null,
  );
  const [franchableName, setFranchableName] = useState('');
  const [step, setStep] = useState<'select' | 'edit'>('select');

  // When modal opens, set initial state based on type
  useEffect(() => {
    if (open) {
      if (type === 'add' && franchise) {
        setSelectedFranchise(franchise);
        setFranchableName('');
        setStep('edit');
      } else if (type === 'edit' && franchise) {
        setSelectedFranchise(franchise);
        setFranchableName(franchise.franchableName || '');
        setStep('edit');
      } else {
        // type === '' — show both steps starting from select
        setStep('select');
      }
    }
  }, [open, type, franchise]);

  const filteredFranchises = data?.franchises || [];

  const resetForm = () => {
    setSearch('');
    setSelectedFranchise(null);
    setFranchableName('');
    setStep('select');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSelectFranchise = (franchise: Franchise) => {
    setSelectedFranchise(franchise);
    setFranchableName(franchise.franchableName || '');
    setStep('edit');
  };

  const handleBack = () => {
    if (type === 'add' || type === 'edit') {
      handleClose();
    } else {
      resetForm();
    }
  };

  const handleSave = () => {
    if (selectedFranchise && franchableName.trim()) {
      onSave(selectedFranchise._id, franchableName.trim());
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      closable
      centered
      className="brand-mapping-modal"
      width={480}
    >
      {step === 'select' ? (
        <div className="brand-mapping-modal__select">
          <Typography.Title level={4} style={{ margin: 0 }}>
            Select a Brand
          </Typography.Title>
          <Typography.Text type="secondary">
            Search and choose a brand to create a new mapping.
          </Typography.Text>

          <Input
            placeholder="Enter franchise name..."
            prefix={
              <span style={{ color: '#9FA1AD', marginRight: 4 }}>
                <img
                  src={searchImg}
                  alt="Search"
                  style={{ width: 16, height: 16, marginTop: 6 }}
                />
              </span>
            }
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginTop: 16 }}
          />

          <div className="brand-mapping-modal__list" >
            {filteredFranchises.map((franchise) => (
              <div
                key={franchise._id}
                className={`brand-mapping-modal__list-item ${
                  selectedFranchise?._id === franchise._id ? 'selected' : ''
                }`}
                onClick={() => handleSelectFranchise(franchise)}
              >
                <span>{franchise.company}</span>
                <span className="tick-mark">
                  <CheckOutlined />
                </span>
                {selectedFranchise?._id === franchise._id && (
                  <CheckOutlined style={{ color: '#1C8271' }} />
                )}
              </div>
            ))}
            {filteredFranchises.length === 0 && (
              <div className="brand-mapping-modal__empty">
                No franchises found.
              </div>
            )}
          </div>

          <div className="brand-mapping-modal__footer">
            <button
              className="brand-mapping-modal__btn-cancel"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="brand-mapping-modal__edit">
          <Typography.Title level={4} style={{ margin: 0 }}>
            {type === 'edit' ? 'Edit Mapping' : 'Add New Mapping'}
          </Typography.Title>
          <Typography.Text type="secondary">
            {type === 'edit'
              ? 'Update the franchable name for this brand.'
              : 'Link this brand to its franchable name.'}
          </Typography.Text>

          <div className="brand-mapping-modal__field" style={{ marginTop: 20 }}>
            <Typography.Text strong>Brand Name</Typography.Text>
            <Input
              value={selectedFranchise?.company || ''}
              disabled
              style={{ marginTop: 4 }}
            />
          </div>

          <div className="brand-mapping-modal__field" style={{ marginTop: 16 }}>
            <Typography.Text strong>Franchable Name</Typography.Text>
            <Input
              value={franchableName}
              onChange={(e) => setFranchableName(e.target.value)}
              placeholder="Enter franchable name..."
              style={{ marginTop: 4 }}
            />
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 4 }}
            >
              This name will be used as the franchable name.
            </Typography.Text>
          </div>

          <div className="brand-mapping-modal__footer">
            <button
              className="brand-mapping-modal__btn-cancel"
              onClick={handleBack}
            >
              {type === 'add' || type === 'edit' ? 'Cancel' : 'Back'}
            </button>
            <button
              className="brand-mapping-modal__btn-save"
              onClick={handleSave}
              disabled={!franchableName.trim() || saving}
            >
              {saving ? 'Saving...' : type === 'edit' ? 'Update Mapping' : 'Save Mapping'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddBrandMappingModal;
